/**
 * @file WmsDashboardMovements.tsx
 * @description Блок «Последние движения» «Обзора склада» (phase19.24 этап 1,
 * шаг 3; phase19.31 шаг О4 — лента в две строки, макеты (в) и (в2)).
 *
 * Строка ленты: сверху дата и время, вид движения и количество со знаком;
 * ниже — товар и склад, справа — номер документа-основания ссылкой (код —
 * моноширинным, остальное — обычным шрифтом, SC43). Подпись операции — из
 * реестра журнала (`movementOperationText`): технические пары бэкфилла —
 * «Корректировка партии», а не «Перемещение (вход)». Время — по часовому
 * поясу магазина (SC31). Пустую ленту блок не показывает — решает страница.
 *
 * Строк — не больше `DASHBOARD_MOVEMENTS_LIMIT` (6): сервер отдаёт больше, и
 * на 1280–1920 правая колонка вытягивалась ниже левой («Что нужно сделать»
 * + пустая «Очередь») — под левой колонкой оставалось ~170px пустоты
 * (дефект визуальной приёмки 2026-10-02). Остальное — по ссылке «Весь журнал».
 */

import { DASHBOARD_TEXT } from "./dashboard-texts";
import { SectionCard } from "../../ui/SectionCard";
import { Skeleton } from "../../ui/skeleton";
import { MovementDocumentLink } from "../MovementDocumentLink";
import { formatSkuName, type StockMovementDto } from "../../../lib/api-wms";
import { movementOperationText } from "../../../lib/movements-texts";
import { formatQtyValue } from "../../../lib/picking-format";
import { formatShopTime } from "../../../lib/shop-time";
import { cn } from "@/lib/utils";

/** Сколько последних движений показывает блок; остальное — «Весь журнал». */
export const DASHBOARD_MOVEMENTS_LIMIT = 6;

interface Props {
  movements: StockMovementDto[];
  /** База маршрута WMS: `/shop/:slug/wms`. */
  basePath: string;
  /** Slug магазина — нужен ссылке на документ-основание. */
  slug?: string;
  loading: boolean;
}

/** Количество со знаком: приход — зелёный, расход — красный. */
function MovementQty({ quantity }: { quantity: string }) {
  const value = Number(quantity);
  return (
    <span
      className={cn(
        "type-body shrink-0 font-medium tabular-nums",
        value >= 0 ? "text-[var(--success)]" : "text-[var(--destructive-text)]",
      )}
    >
      {value > 0 ? "+" : ""}
      {formatQtyValue(quantity)} шт
    </span>
  );
}

export function WmsDashboardMovements({ movements, basePath, slug, loading }: Props) {
  return (
    <SectionCard
      title={DASHBOARD_TEXT.movementsTitle}
      link={{ label: DASHBOARD_TEXT.movementsAll, to: `${basePath}/stock?view=movements` }}
    >
      {loading && movements.length === 0 ? (
        <div className="flex flex-col gap-2" aria-busy="true">
          {[0, 1, 2].map((index) => (
            <Skeleton key={index} className="h-12 rounded-lg" />
          ))}
        </div>
      ) : (
        <ul className="flex flex-col divide-y divide-[var(--border)]">
          {movements.slice(0, DASHBOARD_MOVEMENTS_LIMIT).map((movement) => (
            <li key={movement.id} className="flex min-w-0 flex-col gap-0.5 py-2">
              <span className="flex min-w-0 items-baseline gap-3">
                <span className="type-caption shrink-0 tabular-nums text-on-surface-variant">
                  {formatShopTime(movement.createdAt)}
                </span>
                <span className="type-body min-w-0 flex-1 truncate text-on-surface">
                  {movementOperationText(movement)}
                </span>
                <MovementQty quantity={movement.quantity} />
              </span>
              <span className="flex min-w-0 items-baseline gap-3">
                <span className="type-caption min-w-0 flex-1 truncate text-on-surface-variant">
                  {formatSkuName(movement.sku)} · {movement.warehouse?.name ?? "—"}
                </span>
                {movement.document ? (
                  <span className="shrink-0">
                    <MovementDocumentLink document={movement.document} slug={slug} />
                  </span>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  );
}
